import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import OverviewTab from "./tabs/OverviewTab";
import NotesTab from "./tabs/NotesTab";
import ConversionTab from "./tabs/ConversionTab";
import ActivityTab from "./tabs/ActivityTab";
import { type Lead, STATUS_CFG, fmtDate } from "./types";
import { sourceCfg as getSourceCfg } from "@/pages/admin/leads/components/LeadsDashboard";

type Tab = "overview" | "notes" | "conversion" | "activity";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "overview",   label: "Overview",   icon: "ri-user-line" },
  { id: "notes",      label: "Notes",      icon: "ri-sticky-note-line" },
  { id: "conversion", label: "Conversion", icon: "ri-exchange-line" },
  { id: "activity",   label: "Activity",   icon: "ri-history-line" },
];

export default function LeadDetailPage() {
  const { leadId }  = useParams<{ leadId: string }>();
  const navigate    = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [lead, setLead]         = useState<Lead | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [noteCount, setNoteCount] = useState<number>(0);

  const fetchLead = useCallback(async (tid: string) => {
    setError(null);
    const { data, error: err } = await supabase
      .from("tenant_public_leads")
      .select("*")
      .eq("id", leadId!)
      .eq("tenant_id", tid)
      .maybeSingle();
    if (err)   { setError(err.message); setLoading(false); return; }
    if (!data) { setError("Lead not found."); setLoading(false); return; }
    setLead(data as Lead);
    setLoading(false);
  }, [leadId]);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchLead(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchLead]);

  const handleLeadUpdate = (updated: Partial<Lead>) => {
    setLead((prev) => prev ? { ...prev, ...updated } : prev);
  };

  const handleDelete = async () => {
    if (!tenantId || !lead) return;
    const result = await Swal.fire({
      title: "Delete this lead?",
      text: `"${lead.name}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
    });
    if (!result.isConfirmed) return;
    await supabase.from("tenant_public_leads").delete().eq("id", lead.id).eq("tenant_id", tenantId);
    navigate("/admin/leads");
  };

  /* ── Derived ── */
  const statusCfg = lead ? (STATUS_CFG[lead.status] ?? STATUS_CFG["new"]) : null;
  const sourceCfg = lead ? getSourceCfg(lead.source ?? null) : null;

  return (
    <AdminLayout>
      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={() => tenantId && fetchLead(tenantId)} />
      ) : lead && tenantId ? (
        <>
          {/* ── Top header ── */}
          <div className="bg-white border-b border-slate-200">
            <div className="px-4 md:px-8 pt-5 pb-0">

              {/* Breadcrumb */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => navigate("/admin/leads")}
                  className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-xs transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Leads
                </button>
                <i className="ri-arrow-right-s-line text-slate-300 text-xs"></i>
                <span className="text-slate-500 text-xs truncate max-w-[200px]">{lead.name}</span>
              </div>

              {/* Lead header */}
              <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div className="flex items-start gap-4 min-w-0">
                  {/* Avatar */}
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C8A96E]/15 text-[#9a7840] text-lg font-bold shrink-0">
                    {lead.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h1 className="text-slate-800 font-bold text-xl">{lead.name}</h1>
                      {statusCfg && (
                        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusCfg.badge}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`}></span>
                          {statusCfg.label}
                        </span>
                      )}
                      {sourceCfg && (
                        <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${sourceCfg.cls}`}>
                          <i className={`${sourceCfg.icon} text-[9px]`}></i>
                          {sourceCfg.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="text-slate-400 text-xs hover:text-slate-600 flex items-center gap-1 cursor-pointer transition-colors">
                          <i className="ri-mail-line text-[10px]"></i>
                          <span className="truncate max-w-[180px]">{lead.email}</span>
                        </a>
                      )}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="text-slate-400 text-xs hover:text-slate-600 flex items-center gap-1 cursor-pointer transition-colors">
                          <i className="ri-phone-line text-[10px]"></i>
                          {lead.phone}
                        </a>
                      )}
                      {lead.event_type && (
                        <span className="text-slate-400 text-xs flex items-center gap-1">
                          <i className="ri-calendar-event-line text-[10px]"></i>
                          {lead.event_type}
                          {lead.event_date && <span className="text-slate-300"> · {fmtDate(lead.event_date)}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Header actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {lead.email && (
                    <a href={`mailto:${lead.email}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 hover:bg-slate-50 text-xs font-medium transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-mail-send-line"></i>
                      Email
                    </a>
                  )}
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 hover:bg-slate-50 text-xs font-medium transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-phone-line"></i>
                      Call
                    </a>
                  )}
                  <button
                    onClick={handleDelete}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all cursor-pointer"
                    title="Delete lead"
                  >
                    <i className="ri-delete-bin-6-line text-sm"></i>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-0 overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === tab.id
                        ? "text-[#9a7840] border-[#C8A96E]"
                        : "text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={tab.icon}></i>
                    </span>
                    {tab.label}
                    {tab.id === "notes" && noteCount > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#C8A96E] text-white text-[9px] font-bold">
                        {noteCount > 99 ? "99+" : noteCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Tab content ── */}
          <div className="bg-slate-50 min-h-screen">
            <div className="px-4 md:px-8 py-6 max-w-2xl">
              {activeTab === "overview"   && <OverviewTab   lead={lead} onLeadUpdate={handleLeadUpdate} />}
              {activeTab === "notes"      && <NotesTab      lead={lead} tenantId={tenantId} onNoteCountChange={setNoteCount} />}
              {activeTab === "conversion" && <ConversionTab lead={lead} tenantId={tenantId} onLeadUpdate={handleLeadUpdate} />}
              {activeTab === "activity"   && <ActivityTab   lead={lead} tenantId={tenantId} />}
            </div>
          </div>
        </>
      ) : null}
    </AdminLayout>
  );
}